import React, { useMemo, useEffect, useRef, useCallback, useState } from "react";
import { Flex, ScrollArea } from "@radix-ui/themes";
import { MessageType, Message } from "../../entities/Message";
import { MessageComponent } from "./MessageComponent";
import { CSAT } from "../CSAT";
import { CsatType, SvgRadius } from "../../entities/ChatSettingsV2";

const messageMutators: Record<MessageType, (message: Message) => Message | null> = {
  standard_message: () => null,
  request_email: () => null,
  error: () => null,
  close_chat: () => null,
  connecting_to_agent: () => null,
  agent_joined: () => null,
  file_uploaded: (message) => {
    return {
      ...message,
      action: "standard_message",
      author: "USER",
      data: {
        agentDisplayName: "Quack",
      },
    };
  },
  file: (message) => {
    return {
      ...message,
      action: "standard_message",
      author: message.author,
      data: {
        agentDisplayName: "Quack",
      },
    };
  },
  form_single_select: () => null,
  first_request_email: () => null,
};

function shouldShowMessageAvatar(currentMessage: Message, nextMessage?: Message) {
  if (nextMessage == null) {
    return true;
  }

  const currentMessageAction = currentMessage.action;
  const currentMessageAuthor = currentMessage.author;

  const nextMessageAction = nextMessage.action;
  const nextMessageAuthor = nextMessage.author;

  return !(
    currentMessageAction === nextMessageAction && currentMessageAuthor === nextMessageAuthor
  );
}

function shouldShowMessageTimestamp(currentMessage: Message, nextMessage?: Message) {
  if (nextMessage == null) {
    return true;
  }

  const currentMutator = messageMutators[currentMessage.action];
  const nextMutator = messageMutators[nextMessage.action];

  const mutatedCurrentMessage =
    currentMutator ? (currentMutator(currentMessage) ?? currentMessage) : currentMessage;
  const mutatedNextMessage =
    nextMutator ? (nextMutator(nextMessage) ?? nextMessage) : nextMessage;

  const currentMessageAction = mutatedCurrentMessage?.action;
  const currentMessageAuthor = mutatedCurrentMessage?.author;

  const nextMessageAction = mutatedNextMessage?.action;
  const nextMessageAuthor = mutatedNextMessage?.author;

  if (currentMessageAction !== nextMessageAction || currentMessageAuthor !== nextMessageAuthor) {
    return true;
  }

  const currentMessageCreatedAt = new Date(currentMessage.createdAt);
  const nextMessageCreatedAt = new Date(nextMessage.createdAt);

  return currentMessageCreatedAt.getMinutes() !== nextMessageCreatedAt.getMinutes();
}

function BouncingLoader({ smallPadding }: { smallPadding?: boolean }) {
  return (
    <div
      className="qc-Chat__BouncingLoader"
      style={{ paddingInline: smallPadding ? "8px" : "36px" }}
    >
      <div />
      <div />
      <div />
    </div>
  );
}

interface MessagesProps {
  messages: Message[];
  customSVG: string;
  avatarSVG?: string;
  userAvatarSVG?: string;
  svgRadius?: SvgRadius;
  showCSAT?: boolean;
  showUserBackground?: boolean;
  csatType: CsatType;
  allowCsatComment?: boolean;
  csatCommentText?: string;
  thinking?: boolean;
  csatScore?: number;
  csatComment?: string;
  isLoading?: boolean;
  onCSATScore: (score: number, comment?: string) => void;
  onEscalate: () => Promise<void>;
  onEscalationEmail: (email: string) => Promise<void>;
  onFirstEscalationEmail: (email: string) => Promise<void>;
  tenant: string;
  quackAgentName: string;
  chatEnded: boolean;
  sendFormOptionSelected: (formId: string, option: string) => Promise<void>;
  sendMessage?: (message: string, file?: File) => Promise<void>;
  chatSessionId?: string;
  getJourney?: () => any[];
  getDom?: () => string;
  onClose?: () => void;
  userEmail?: string;
  contextArrowsHelperConfiguration?: {
    isToggleOn: boolean;
    emails: string[];
  };
  track?: (eventName: string, properties: any) => void;
}

export function Messages({
  messages,
  customSVG,
  avatarSVG,
  userAvatarSVG,
  showUserBackground = false,
  svgRadius,
  showCSAT,
  allowCsatComment,
  csatCommentText,
  csatType,
  thinking,
  csatScore,
  csatComment,
  isLoading,
  onCSATScore,
  onEscalate,
  onEscalationEmail,
  onFirstEscalationEmail,
  tenant,
  quackAgentName,
  chatEnded,
  sendFormOptionSelected,
  sendMessage,
  chatSessionId,
  getJourney,
  getDom,
  onClose,
  userEmail,
  contextArrowsHelperConfiguration,
  track,
}: MessagesProps) {
  const [isTyping, setIsTyping] = useState(false);
  const [animatedMessages, setAnimatedMessages] = useState<Set<string>>(new Set());
  const previousMessagesRef = useRef<Message[]>([]);

  const uniqueMessages = useMemo<Message[]>(() => {
    const messageMap = new Map<string, Message>();

    messages.forEach((message) => {
      const existingMessage = messageMap.get(message.deduplicationKey);

      if (!existingMessage || new Date(message.createdAt) > new Date(existingMessage.createdAt)) {
        messageMap.set(message.deduplicationKey, message);
      }
    });

    return Array.from(messageMap.values());
  }, [messages]);

  const visibleMessages = useMemo<Message[]>(() => {
    if (isLoading) {
      return [];
    }

    const hasAgentJoined = uniqueMessages.some((message) => message.action === "agent_joined");

    const submittedFormsIds: string[] = [];
    uniqueMessages.forEach((message) => {
      if (message.action === "standard_message" && message.data?.formId != null) {
        submittedFormsIds.push(message.data.formId);
      }
    });

    const submittedFormsIdSet = new Set(submittedFormsIds);

    const visibleMessages: Message[] = [];
    uniqueMessages.forEach((message) => {
      if (message.action === "connecting_to_agent" && hasAgentJoined) {
        return;
      }

      if (message.action === "form_single_select" && submittedFormsIdSet.has(message.data.formId)) {
        return;
      }

      visibleMessages.push(message);
    });

    return visibleMessages;
  }, [uniqueMessages, isLoading]);

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [uniqueMessages, showCSAT, thinking, scrollToBottom, csatScore, csatComment]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (thinking) {
      timeoutId = setTimeout(() => {
        setIsTyping(true);
      }, 8 * 1000);
    } else {
      setIsTyping(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [thinking]);

  useEffect(() => {
    const previousMessages = previousMessagesRef.current;
    const previousMessageIds = new Set(previousMessages.map((msg) => msg.deduplicationKey));

    const newMessages = visibleMessages.filter(
      (msg) => !previousMessageIds.has(msg.deduplicationKey),
    );
    const newMessageIds = newMessages.map((msg) => msg.deduplicationKey);

    if (newMessageIds.length > 0) {
      setAnimatedMessages((prev) => new Set([...prev, ...newMessageIds]));

      newMessageIds.forEach((id) => {
        setTimeout(() => {
          setAnimatedMessages((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, 300);
      });
    }

    previousMessagesRef.current = visibleMessages;
  }, [visibleMessages]);

  return (
    <Flex flexGrow={"1"} direction={"column"} overflow={"hidden"}>
      <ScrollArea ref={scrollAreaRef} scrollbars={"vertical"} style={{ width: "100%" }}>
        <Flex
          p={"4"}
          direction={"column"}
          gap={"3"}
          className="qc-Chat__Messages_Viewport"
          role="log"
        >
          {isLoading && (
            <Flex p={"4"}>
              <BouncingLoader smallPadding />
            </Flex>
          )}

          {visibleMessages
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
            .map((message, index, array) => (
              <div
                key={index}
                className={
                  animatedMessages.has(message.deduplicationKey) ? "qc-message-animate" : ""
                }
              >
                <MessageComponent
                  {...message}
                  onEscalate={onEscalate}
                  onEscalationEmail={onEscalationEmail}
                  onFirstEscalationEmail={onFirstEscalationEmail}
                  customSVG={avatarSVG ?? customSVG}
                  userAvatarSVG={userAvatarSVG}
                  showUserBackground={showUserBackground}
                  svgRadius={svgRadius}
                  isLastMessage={index === visibleMessages.length - 1}
                  showAvatar={shouldShowMessageAvatar(message, array[index + 1] ?? undefined)}
                  showTimestamp={shouldShowMessageTimestamp(message, array[index + 1] ?? undefined)}
                  thinking={thinking ?? false}
                  tenant={tenant}
                  quackAgentName={quackAgentName}
                  chatEnded={chatEnded}
                  sendFormOptionSelected={sendFormOptionSelected}
                  chatSessionId={chatSessionId}
                  getJourney={getJourney}
                  getDom={getDom}
                  onClose={onClose}
                  userEmail={userEmail}
                  contextArrowsHelperConfiguration={contextArrowsHelperConfiguration}
                  track={track}
                />
              </div>
            ))}

          {thinking && isTyping && (
            <div style={{ display: "flex", alignItems: "baseline" }}>
              <span style={{ color: "var(--textPlaceholder)", fontSize: "12px" }}>Typing</span>{" "}
              <BouncingLoader smallPadding />
            </div>
          )}
          {showCSAT && (
            <CSAT
              csatScore={csatScore}
              csatComment={csatComment}
              csatType={csatType}
              allowCsatComment={allowCsatComment}
              csatCommentText={csatCommentText}
              onCSATScore={onCSATScore}
              onScrollToBottom={scrollToBottom}
            />
          )}
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
